import { createContext, ReactNode, useState, useEffect, useContext, useCallback } from "react"
import { PoapList } from "../domain/poap/Models/PoapList"
import { useEthereum } from "./EthereumContext"
import { useTransactions } from "./TransactionsContext"
import { DomainEventDetail, DomainEvents } from "../domain/_kernel/Events"
import { TransactionType } from "../domain/ethereum/Models/Transaction"

interface PoapContextInterface {
  userPoapList: PoapList
  isLoading: boolean
  isPendingMintPoap: boolean
}

const initialState: PoapContextInterface = {
  userPoapList: PoapList.empty(),
  isLoading: false,
  isPendingMintPoap: false,
}

interface PoapProviderProps {
  children: ReactNode
}

const Context = createContext<PoapContextInterface>(initialState)

export const PoapContext = ({ children }: PoapProviderProps) => {
  const [userPoapList, setUserPoapList] = useState<PoapList>(initialState.userPoapList)
  const [isLoading, setIsLoading] = useState<boolean>(initialState.isLoading)
  const [isPendingMintPoap, setIsPendingMintPoap] = useState<boolean>(initialState.isPendingMintPoap)

  const { isWalletConnected, waitForNextBlockNumber } = useEthereum()
  const { transactions } = useTransactions()

  const updateUserPoapList = useCallback(() => {
    setIsLoading(true)
    window.domain.GetUserPoapListUseCase.execute()
      .then(setUserPoapList)
      .catch((err) => {
        console.error("Error loading user POAPs:", err)
        setUserPoapList(PoapList.empty())
      })
      .finally(() => setIsLoading(false))
  }, [])

  const waitAndUpdateUserPoapList = useCallback(async () => {
    await waitForNextBlockNumber()
    await updateUserPoapList()
    setIsPendingMintPoap(false)
  }, [updateUserPoapList, waitForNextBlockNumber])

  useEffect(() => {
    if (isWalletConnected) {
      updateUserPoapList()
    } else {
      setUserPoapList(PoapList.empty())
    }
  }, [isWalletConnected, updateUserPoapList])

  useEffect(() => {
    if (transactions.isPendingMintPoap) setIsPendingMintPoap(true)
  }, [transactions.isPendingMintPoap])

  // Handle Success Transaction
  const handleSuccessTransaction = useCallback(
    (e: CustomEvent<DomainEventDetail[DomainEvents.SUCCESS_TRANSACTION]>) => {
      const { type } = e.detail.transaction
      if (type === TransactionType.MINT_POAP) {
        waitAndUpdateUserPoapList()
      }
    },
    [waitAndUpdateUserPoapList],
  )

  // Events
  useEffect(() => {
    window.addEventListener(DomainEvents.SUCCESS_TRANSACTION, handleSuccessTransaction as EventListener)
    return () => window.removeEventListener(DomainEvents.SUCCESS_TRANSACTION, handleSuccessTransaction as EventListener)
  }, [handleSuccessTransaction])

  return (
    <Context.Provider
      value={{
        userPoapList,
        isLoading,
        isPendingMintPoap,
      }}
    >
      {children}
    </Context.Provider>
  )
}

export const usePoaps = function () {
  const context = useContext(Context)
  if (context === undefined) {
    throw new Error(`usePoaps must be used within a PoapContextProvider`)
  }
  return context
}
